import { GridApi, GridReadyEvent } from '@ag-grid-community/core';
import classNames from 'classnames/bind';
import { AGTable, AGTableProps } from '@creditsights/styleguide-web';
import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import styles from './FullPage.module.scss';

const cx = classNames.bind(styles);

export interface FullPageTableProps<TData> extends AGTableProps<TData> {
  pageTitle: ReactNode;
  paginationComponent?: React.ReactElement;
  headerComponent?: React.ReactElement;
}
const DEFAULT_NAVBAR_HEIGHT = 104;
const getApplicationHeaderHeight = () =>
  document.getElementsByTagName('header')[0]?.clientHeight ?? DEFAULT_NAVBAR_HEIGHT;

const FullPageTable = <TData,>({
  pageTitle,
  onGridReady,
  headerComponent,
  ...props
}: FullPageTableProps<TData>): React.ReactElement => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const gridApiRef = useRef<GridApi>();
  const lastTableContainerTopRef = useRef<number>();
  const lastTableContainerHeightRef = useRef<number>();

  useEffect(() => {
    gridApiRef.current?.sizeColumnsToFit();
  }, [props.columnDefs]);

  const calcBlocks = () => {
    if (tableWrapperRef.current && tableContainerRef.current) {
      const tableTop = tableWrapperRef.current.getBoundingClientRect().top;
      const applicationHeaderHeight = getApplicationHeaderHeight();

      if (lastTableContainerTopRef.current !== applicationHeaderHeight) {
        lastTableContainerTopRef.current = applicationHeaderHeight;
        tableContainerRef.current.style.top = `${applicationHeaderHeight}px`;
      }

      const nextTableContainerTop = Math.max(applicationHeaderHeight, tableTop);
      if (lastTableContainerHeightRef.current !== nextTableContainerTop) {
        lastTableContainerHeightRef.current = nextTableContainerTop;
        tableContainerRef.current.style.height = `calc(100vh - ${nextTableContainerTop}px - 100px)`;
      }
    }
  };

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event.api;
      onGridReady?.(event);

      // resizeColumnsToFit();
      calcBlocks();
    },
    [onGridReady]
  );

  return (
    <div className={cx('body')}>
      <div className={cx('full-page-container')}>
        <div className={cx('content-container')}>
          <div className={cx('header')}>
            <h1>{pageTitle}</h1>
            <div className={cx('sub-header')}>{headerComponent}</div>
          </div>
          <div className={cx('content')}>
            <div ref={tableWrapperRef}>
              <div
                className={classNames(cx('table-container'), 'ag-theme-alpine')}
                data-testid="table"
                ref={tableContainerRef}
              >
                <AGTable
                  className="ag-table"
                  onGridReady={handleGridReady}
                  headerHeight={40}
                  rowHeight={33}
                  suppressCellFocus
                  suppressMultiSort
                  suppressDragLeaveHidesColumns
                  suppressContextMenu
                  enableCellTextSelection
                  unSortIcon
                  maintainColumnOrder
                  {...props}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullPageTable;
